import { useTranslation } from 'react-i18next'

import styles from './StepVisualSequences.module.scss'

/**
 * Шаг 05 — «Мы предлагаем Sequences».
 *
 * ЕДИНСТВЕННАЯ миниатюра в этой папке, нарисованная не с работающего экрана. Когда её рисовали, в
 * движке была одна точка касания — `POST /api/runs/:id/draft` с ОДНИМ первым сообщением в двух
 * вариантах, — и ни расписания, ни follow-up, ни маршрута последовательности не существовало;
 * поэтому карточка вешает поверх неё бейдж «скоро».
 *
 * УСТАРЕЛО С 2026-09-15, и это надо знать, прежде чем ссылаться на абзац выше: цепочка теперь есть.
 * `server/src/engine/plan/plan.ts` собирает `RowPlan` — узлы, рёбра, каденцию, — `POST
 * /api/runs/:id/plan` строит её под конкретного человека, `/plan/cadence` пересобирает дни,
 * `/plan/draft` пишет отдельный follow-up, а экран редактора (`launch/plan/SequenceView`) открыт
 * ДО оплаты. Значит: (1) миниатюра больше не рисует несуществующее, но и не снята с настоящего
 * экрана — за ней стоит настоящий редактор, с которого её стоит перерисовать; (2) бейдж «скоро» на
 * карточке лендинга — обещание, которое продукт уже сдержал, и снимать его или нет, решает продукт,
 * а не эта миниатюра; (3) старое правило про слово «сиквенс» («обещает каденцию, которой нет»)
 * больше не действует — каденция есть и её видно на экране.
 *
 * Что взято с НАСТОЯЩИХ экранов, чтобы зритель узнал продукт, а не прочитал схему:
 *   шапка       — ранг в --acc-soft, имя, должность verbatim через точку: `LeadCard` `.rank`,
 *                 `.name`, `.role`, один в один по порядку
 *   первое      — блок `.draft` из `LeadCard.module.scss`: золотая обводка и золотая заглавная
 *   касание       подпись «FIRST MESSAGE» (`app.lead.firstMessage`), потому что золото в продукте —
 *                 цвет всего, что за платной дверью
 *   цитата      — лиловая, --say/--say-soft: в токенах лиловый закреплён за тем, что сказал САМ
 *                 ЧЕЛОВЕК, зелёный — за нашим выводом. Смешать их значит выдать цитату за наш вывод
 *   текст       — не сочинён: с 2026-10-13 это рамка «нетворкинг», которой AI Sales пишет на проде с 09.10
 *                 (OUTREACH_FRAME=network): повод — дословно из `engine/touch/networkLines.ts` (reason),
 *                 первая строка напоминания — из `engine/plan/followUpLines.ts` (opener). До этого здесь стояли
 *                 строки консьерж-канона («I manage member support at Intch»): миниатюра стоит на вкладке AI Sales
 *                 в листе тарифов, а AI Sales пишет от имени покупателя, не сотрудника Intch
 *
 * ОГОВОРКА к тексту follow-up (до 2026-10-13). Строка напоминания была одолжена из D1 консьерж-канона («Is that
 * search…»), потому что строк напоминаний в каноне не было. Теперь они есть у самого движка (followUpLines.ts), и
 * миниатюра берёт первую фразу оттуда же, откуда её берёт настоящее напоминание.
 *
 * Язык интерфейса английский, как во всех соседних миниатюрах: продукт англоязычный, и один
 * русский чип в ряду из шести кадров читался бы как другой экран.
 *
 * ПРИДУМАНО, потому что иначе нечего показать: вертикальная лента, подписи интервалов и две строки
 * follow-up. Нарисованы как самое скромное из возможных обещаний — список касаний с датами и
 * статусом. Намеренно НЕ нарисовано: ответ собеседника, инбокс, «получен ответ», автоответ, значок
 * почтовика или CRM. Любое из этого рекламировало бы интеграцию, которой нет.
 *
 * Третье касание — серые полоски, а не текст. Follow-up через две недели ещё не написан, и
 * подставить туда фразу значило бы заявить, что продукт заранее пишет всю каденцию.
 *
 * Правый верхний угол ОТДАН бейджу «скоро»: в `HowItWorksDraft.module.scss` он прибит на
 * top 18 / right 20 карточки и ложится ровно на строку шапки. Сам он пустым не остаётся — шапка
 * набита влево и чип доезжает туда, — поэтому полосу под бейдж отнимает `padding-right` у `.head`.
 *
 * Текст захардкожен, без `t()`: песочница не собирается в прод, и переводить рисунок, до которого
 * не доходит ни один посетитель, значило бы только добавить ключи в оба файла локалей.
 */
export const StepVisualSequences = () => {
  const { t } = useTranslation()
  /* День — иллюстративные данные, месяц — подпись: «12 Aug» в русской локали читается как чужое
     вкрапление, а «12 авг» — нет. Поэтому склеиваем, а не держим дату одной строкой. */
  const aug = (day: number) => `${day} ${t('landing.how.cards.monthAug')}`

  return (
    <div className={styles.root}>
      {/* Шапка карточки лида, ужатая примерно втрое. */}
      <div className={styles.head}>
        <span className={styles.rank}>1</span>
        <span className={styles.name}>Reagan M.</span>
        <span className={styles.role}>Head of People Ops</span>
        <span className={styles.chain}>{t('landing.how.cards.sequenceTouches', { count: 3 })}</span>
      </div>

      <div className={styles.rail}>
        {/* ---- 01: единственное, что продукт умеет писать сегодня ---- */}
        <div className={`${styles.step} ${styles.stepFirst}`}>
          <span className={`${styles.dot} ${styles.dotSent}`} />
          <div className={`${styles.card} ${styles.cardDraft}`}>
            <div className={styles.cardTop}>
              <b className={`${styles.label} ${styles.labelGold}`}>{t('app.lead.firstMessage')}</b>
              <span className={`${styles.when} ${styles.whenSent}`}>{t('landing.how.cards.sentOn', { date: aug(12) })}</span>
            </div>
            <div className={styles.line}>Hi Reagan! I'm growing my network on Intch.</div>
            <div className={styles.line}>
              You mention <q className={styles.quote}>Strengthen employee relations</q>
            </div>
          </div>
        </div>

        <div className={styles.gap}>
          <span className={styles.gapText}>{t('landing.how.cards.gapDays', { count: 3 })}</span>
        </div>

        {/* ---- 02: написано, ждёт даты ---- */}
        <div className={styles.step}>
          <span className={`${styles.dot} ${styles.dotNext}`} />
          <div className={styles.card}>
            <div className={styles.cardTop}>
              <b className={styles.label}>{t('landing.how.cards.followUp', { n: 1 })}</b>
              <span className={`${styles.when} ${styles.whenNext}`}>{t('landing.how.cards.scheduledOn', { date: aug(15) })}</span>
            </div>
            <div className={styles.line}>I don't know whether my first message reached you.</div>
          </div>
        </div>

        <div className={styles.gap}>
          <span className={styles.gapText}>{t('landing.how.cards.gapDays', { count: 7 })}</span>
        </div>

        {/* ---- 03: слот, а не сообщение. См. заметку наверху ---- */}
        <div className={`${styles.step} ${styles.stepLast}`}>
          <span className={`${styles.dot} ${styles.dotIdle}`} />
          <div className={`${styles.card} ${styles.cardIdle}`}>
            <div className={styles.cardTop}>
              <b className={styles.label}>{t('landing.how.cards.followUp', { n: 2 })}</b>
              <span className={`${styles.when} ${styles.whenIdle}`}>{t('landing.how.cards.scheduledOn', { date: aug(22) })}</span>
            </div>
            <div className={styles.bars} aria-hidden="true">
              <i style={{ width: '72%' }} />
              <i style={{ width: '44%' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
